import {
    ModifierOptionContract,
    OrderContract,
    OrderLineKdsStatus,
    OrderLineLookupContract,
    OrderLineStatus,
    OrderPaymentStatus,
    OrderLookupContract,
    OrderStatus,
    OrderLineContract,
    ItemContract,
    VoidCompReasonModel,
    VoidCompReasonType,
    OrderFulfillmentStatus,
    OrderOwnerContract,
} from 'models'
import { OPEN_ITEM_ID } from 'constant/magicNumber'
import { OrderLineNotificationContract } from 'hooks/useOrderNotification/OrderLineNotificationContract'

export function isSentOrder(order: OrderContract | undefined) {
    return order?.status === OrderStatus.Sent
}
export function isNewOrder(order: OrderContract | undefined) {
    return (
        order?.status === OrderStatus.New ||
        order?.status === OrderStatus.Accepted ||
        order?.status === OrderStatus.Saved ||
        order?.status === OrderStatus.Sent
    )
}
export function isOrderHasPayment(order: OrderContract | undefined) {
    return isPartiallyPaidOrder(order) || isPaidOrRefundedOrder(order)
}
export function isPartiallyPaidOrder(order: OrderContract | undefined) {
    return order?.orderPaymentStatus === OrderPaymentStatus.PartiallyPaid
}
export function isPaidOrRefundedOrder(order: OrderContract | undefined) {
    return (
        order?.orderPaymentStatus === OrderPaymentStatus.Paid ||
        order?.orderPaymentStatus === OrderPaymentStatus.Refunded
    )
}
export const isComp = (voidComp?: VoidCompReasonModel) =>
    (voidComp && voidComp.type === VoidCompReasonType.Comp) || false

export const getModOptionName = (option: ModifierOptionContract) => {
    return option?.displayName ?? option?.itemName ?? '#unnamed'
}

type TItemName = Pick<ItemContract, 'name' | 'displayName'>
export const displayItemName = (
    item?: TItemName & {
        baseItem?: TItemName | null
    },
    isSharedLine: boolean = false,
) => {
    return (
        (item?.baseItem?.displayName ||
            item?.baseItem?.name ||
            item?.displayName ||
            item?.name ||
            '#unnamed') + (isSharedLine ? ' (Shared)' : '')
    )
}

export const displayOrderLineItemName = (
    orderLine: Pick<
        OrderLineLookupContract,
        'itemID' | 'item' | 'displayName' | 'sharedOrderLineID'
    >,
) => {
    const item = orderLine?.item
    const isSubSharedLine = Boolean(orderLine?.sharedOrderLineID)
    const name = displayItemName(
        { ...item, displayName: orderLine.displayName ?? '' },
        isSubSharedLine,
    )
    return orderLine.itemID?.includes(OPEN_ITEM_ID)
        ? `${name} (Open Item)`
        : name
}

/**
 * Return TRUE if order is fall in one of below conditions
 * 1) Order status is not either `New` or `Saved` or `Sent`
 * 2) Order is paid or partially paid
 */
export const isProcessedOrder = (
    editingOrder: OrderContract | OrderLookupContract | undefined,
) => {
    if (editingOrder == null) return false
    return !isNewOrder(editingOrder) || isOrderHasPayment(editingOrder)
}

export const isOrderNotComplete = (order: OrderLookupContract) => {
    return (
        !(
            order!.orderPaymentStatus === OrderPaymentStatus.Paid ||
            order!.orderPaymentStatus === OrderPaymentStatus.Refunded ||
            order!.orderPaymentStatus === OrderPaymentStatus.Voided
        ) ||
        order?.orderLines?.some(
            (i) =>
                (!i.isSharedLine ||
                    (i.isSharedLine &&
                        i.subSharedOrderLines?.some(
                            (s) =>
                                (s.status !== OrderLineStatus.Voided &&
                                    s.status !== OrderLineStatus.Returned &&
                                    s.status !== OrderLineStatus.Completed) ||
                                (s.kdsStatus !== OrderLineKdsStatus.Finished &&
                                    s.kdsStatus !==
                                        OrderLineKdsStatus.Cancelled),
                        ))) &&
                ((i.status !== OrderLineStatus.Voided &&
                    i.status !== OrderLineStatus.Returned &&
                    i.status !== OrderLineStatus.Completed) ||
                    (i.kdsStatus !== OrderLineKdsStatus.Finished &&
                        i.kdsStatus !== OrderLineKdsStatus.Cancelled)),
        )
    )
}

export function isSentLine(line: OrderLineContract) {
    return line.kdsStatus !== OrderLineKdsStatus.Unknown
}

export function getOrderFulfillmentStatus(
    lines: OrderLineNotificationContract[],
): OrderFulfillmentStatus | undefined {
    return lines.every(
        (a) =>
            a.kdsStatus === OrderLineKdsStatus.Finished ||
            a.kdsStatus === OrderLineKdsStatus.Cancelled,
    )
        ? OrderFulfillmentStatus.Fulfilled
        : lines.some(
                (a) =>
                    a.kdsStatus === OrderLineKdsStatus.AwaitingPreparation ||
                    a.kdsStatus === OrderLineKdsStatus.InPreparation,
            )
          ? OrderFulfillmentStatus.InPreparation
          : OrderFulfillmentStatus.Unfulfilled
}

export const isHasOnlySharedOrderLines = (
    orderLines: OrderLineLookupContract[],
) => orderLines.every((i) => i.isSharedLine)

export const isAllVoidedOrderLines = (
    order: OrderOwnerContract | undefined,
    orderLines: OrderLineLookupContract[],
) =>
    (order?.isVoid ?? false) ||
    orderLines.every((i) => i.status === OrderLineStatus.Voided)

export const isCompleteOrder = (
    order: Pick<OrderContract, 'status'> | undefined,
) =>
    order?.status === OrderStatus.Completed ||
    order?.status === OrderStatus.Voided ||
    order?.status === OrderStatus.Cancelled ||
    order?.status === OrderStatus.Rejected
